import {
  doc,
  getDoc,
  DocumentReference,
  collection,
  query,
  orderBy,
  getDocs,
  where,
  onSnapshot
} from "firebase/firestore";
import { firestore as db } from "@/utils/firebase";
import { Session, SessionSummary } from "./types";

// Get typed document reference
const getSessionRef = (
  userId: string,
  sessionId: string
): DocumentReference<Session> =>
  doc(db, "users", userId, "sessions", sessionId) as DocumentReference<Session>;

/**
 * Get a single session document by ID
 */
export const getSession = async (
  userId: string,
  sessionId: string
): Promise<Session | null> => {
  const docSnap = await getDoc(getSessionRef(userId, sessionId));
  return docSnap.exists() ? docSnap.data() : null;
};

/**
 * Get all sessions for a user that have at least one user message, sorted by most recent first
 * Uses hasUserMessages flag for efficient filtering
 */
export const getSessions = async (userId: string): Promise<Session[]> => {
  const sessionsRef = collection(db, "users", userId, "sessions");
  const q = query(
    sessionsRef,
    where("title", "!=", null),
    orderBy("updatedAt", "desc")
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data()) as Session[];
};

/**
 * Get session summary for a specific session
 * Path: /users/{userId}/sessionInsights/{sessionId}
 */
export const getSessionSummary = async (
  userId: string,
  sessionId: string
): Promise<SessionSummary | null> => {
  const summaryRef = doc(
    db,
    "users",
    userId,
    "sessionInsights",
    sessionId
  ) as DocumentReference<SessionSummary>;
  const docSnap = await getDoc(summaryRef);
  return docSnap.exists() ? docSnap.data() : null;
};

/**
 * Subscribe to real-time updates for session summary
 * Path: /users/{userId}/sessionInsights/{sessionId}
 *
 * @param userId - The user ID
 * @param sessionId - The session ID
 * @param onSuccess - Callback when summary is successfully loaded or updated
 * @param onError - Callback when an error occurs
 * @returns Unsubscribe function to stop listening
 */
export const subscribeToSessionSummary = (
  userId: string,
  sessionId: string,
  onSuccess: (summary: SessionSummary | null) => void,
  onError: (error: Error) => void
): (() => void) => {
  const summaryRef = doc(
    db,
    "users",
    userId,
    "sessionInsights",
    sessionId
  ) as DocumentReference<SessionSummary>;

  return onSnapshot(
    summaryRef,
    (docSnap) => {
      if (docSnap.exists()) {
        onSuccess(docSnap.data());
      } else {
        onSuccess(null);
      }
    },
    onError
  );
};
